import { useState, useMemo, useEffect, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import ConfirmDialog from '@/components/feature/ConfirmDialog';
import { useToast } from '@/contexts/ToastContext';
import { MasterFilters, MasterStatsRow } from '@/pages/masters/common/commonComponents';
import { getAllRoutings, deleteRouting, createRouting } from '@/api/routing.api';
import { getAllItems } from '@/api/item.api';
import { getAllWorkCenters, type WorkCenterResponse } from '@/api/workcenter.api';
import { useWarehouseStore } from '@/stores/warehouseStore';
import { stageHourlyCost, stageUnitCost, stageSetupCost, runMinutesPerUnit, money } from './stageCost';

interface Routing {
    id: string;
    name: string;
    code: string;
    itemId: string | null;
    itemName: string | null;
    itemIds?: string[];
    version: string;
    status: 'ACTIVE' | 'DRAFT' | 'OBSOLETE';
    stages: any[];
    totalTimeMinutes: number;
    createdAt: string;
    isActive: boolean;
    warehouseId?: string | null;
}

function formatMinutes(total: number) {
    const rounded = Math.round(total * 100) / 100;
    const hrs = Math.floor(rounded / 60);
    const mins = Math.round((rounded % 60) * 100) / 100;
    if (hrs === 0) return `${mins} min`;
    if (mins === 0) return `${hrs} hrs`;
    return `${hrs} hrs ${mins} min`;
}

/** A saved stage, read the same way whichever key names it was saved with */
interface StageView {
    seq: number;
    name: string;
    stageType: 'IN_HOUSE' | 'JOB_WORK';
    subcontractorName: string | null;
    jobRate: number;
    workCenterId: string;
    workCenterName: string;
    machineId: string;
    machineName: string;
    operatorsRequired: number;
    setupMinutes: number;
    runMinutes: number;
    timeBasis: 'PER_UNIT' | 'PER_BATCH';
    batchSize: number;
    qcRequired: boolean;
    qcChecklistName: string | null;
    materials: number;
    status: 'ACTIVE' | 'DRAFT';
}

const firstNum = (...vals: any[]) => {
    for (const v of vals) {
        if (v === undefined || v === null || v === '') continue;
        const n = Number(v);
        if (Number.isFinite(n)) return n;
    }
    return 0;
};

function toStageView(st: any, idx: number): StageView {
    const materials = Array.isArray(st.bom_component_ids) ? st.bom_component_ids.length
        : Array.isArray(st.bomComponentIds) ? st.bomComponentIds.length : 0;
    return {
        seq: firstNum(st.sequence, st.sequence_no, st.stageNumber, st.stage_number) || idx + 1,
        name: st.stageName || st.operationName || st.stage_name || `Stage ${idx + 1}`,
        stageType: String(st.stageType || st.stage_type || '').toUpperCase() === 'JOB_WORK' ? 'JOB_WORK' : 'IN_HOUSE',
        subcontractorName: st.subcontractorName || st.subcontractor_name || null,
        jobRate: firstNum(st.jobRate, st.job_rate),
        workCenterId: String(st.workCenterId || st.work_center_id || ''),
        workCenterName: st.workCenterName || st.work_center_name || '',
        machineId: String(st.machineId || st.machine_id || ''),
        machineName: st.machineName || st.machine_name || '',
        operatorsRequired: Math.max(1, firstNum(st.operatorsRequired, st.operators_required) || 1),
        setupMinutes: firstNum(st.setupTimeMinutes, st.setup_time_minutes, st.setupMinutes),
        runMinutes: firstNum(st.runTimeMinutes, st.standardTimeMinutes, st.time_per_unit_minutes, st.runMinutes),
        timeBasis: String(st.timeBasis || st.time_basis || '').toUpperCase() === 'PER_BATCH' ? 'PER_BATCH' : 'PER_UNIT',
        batchSize: Math.max(1, firstNum(st.batchSize, st.batch_size) || 1),
        qcRequired: Boolean(st.qcRequired ?? st.qc_enabled ?? st.requires_qc),
        qcChecklistName: st.qcChecklistName || st.checklist_name || null,
        materials,
        status: st.status === 'DRAFT' || (!st.status && materials === 0) ? 'DRAFT' : 'ACTIVE',
    };
}

const costInput = (s: StageView) => ({
    machineId: s.machineId, operatorsRequired: s.operatorsRequired, stageType: s.stageType,
    runMinutes: s.runMinutes, setupMinutes: s.setupMinutes, timeBasis: s.timeBasis, batchSize: s.batchSize, jobRate: s.jobRate,
});

const WC_TYPE_LABEL: Record<string, string> = { MANUAL: 'Labour', LABOR: 'Labour', MACHINE: 'Machine', BOTH: 'Machine + Labour' };

const STATUS_BADGE: Record<string, { cls: string; dot: string }> = {
    ACTIVE: { cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500' },
    DRAFT: { cls: 'bg-amber-50 text-amber-700 border-amber-200', dot: 'bg-amber-500' },
    OBSOLETE: { cls: 'bg-slate-100 text-slate-600 border-slate-200', dot: 'bg-slate-400' },
};

export default function RoutingPage() {
    const navigate = useNavigate();
    const toast = useToast();
    const [routings, setRoutings] = useState<Routing[]>([]);
    const [itemsList, setItemsList] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState<'ALL' | 'ACTIVE' | 'DRAFT' | 'OBSOLETE'>('ALL');
    const [itemFilter, setItemFilter] = useState<string>('ALL');
    const [deleteConfirm, setDeleteConfirm] = useState<Routing | null>(null);
    const [workCenters, setWorkCenters] = useState<WorkCenterResponse[]>([]);
    const [expanded, setExpanded] = useState<Set<string>>(new Set());

    const { selectedWarehouseId } = useWarehouseStore();

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const [rRes, iRes, wcRes] = await Promise.all([getAllRoutings(), getAllItems(), getAllWorkCenters().catch(() => null)]);
            if (wcRes?.success && wcRes.data) setWorkCenters(wcRes.data);
            if (rRes.success && rRes.data) {
                const mapped = rRes.data.map((r: any) => ({
                    id: r.id,
                    name: r.name,
                    code: r.code,
                    itemId: Array.isArray(r.item_id) ? r.item_id.join(', ') : (r.item_id || ''),
                    // item_ids is the routing's item list; item_id is the older copy and can be empty
                    itemIds: Array.isArray(r.item_ids) && r.item_ids.length > 0
                        ? r.item_ids
                        : (Array.isArray(r.item_id) ? r.item_id : []),
                    itemName: Array.isArray(r.item_name) ? r.item_name.join(', ') : (r.item_name || ''),
                    version: r.version,
                    status: r.status,
                    stages: (typeof r.stages === 'string' ? JSON.parse(r.stages) : r.stages) || [],
                    totalTimeMinutes: r.total_time_minutes,
                    createdAt: r.created_at,
                    isActive: r.is_active,
                    warehouseId: r.warehouse_id || null,
                }));
                setRoutings(mapped);
            }
            if (iRes.success && iRes.data) {
                setItemsList(iRes.data);
            }
        } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Failed to fetch routings');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const warehouseRoutings = useMemo(() => {
        if (!selectedWarehouseId) return routings;
        return routings.filter((r) => r.warehouseId === selectedWarehouseId);
    }, [routings, selectedWarehouseId]);

    const filteredItemsList = useMemo(() => {
        // Routings are made for Finished / Semi-Finished goods; an item some routing already carries
        // stays listed so that routing can still be found
        const linked = new Set(routings.flatMap((r) => r.itemIds || []).map(String));
        const madeItem = (i: any) => {
            const t = String(i.item_type || i.category || i.type || '').toUpperCase().replace(/[\s-]/g, '_');
            return t === 'FINISHED_GOOD' || t === 'SEMI_FINISHED';
        };
        let list = itemsList.filter((i) => (i.is_active || i.isActive) && (madeItem(i) || linked.has(String(i.id))));
        if (selectedWarehouseId) {
            list = list.filter((i) => i.warehouse_id === selectedWarehouseId || i.warehouseId === selectedWarehouseId || i.warehouseid === selectedWarehouseId);
        }
        return list;
    }, [itemsList, selectedWarehouseId, routings]);

    const filtered = useMemo(() => {
        const q = search.toLowerCase().trim();
        return warehouseRoutings.filter((r) => {
            const matchSearch =
                !q ||
                r.name.toLowerCase().includes(q) ||
                r.code.toLowerCase().includes(q) ||
                (r.itemName && r.itemName.toLowerCase().includes(q));
            const matchStatus = statusFilter === 'ALL' || r.status === statusFilter;
            const matchItem = itemFilter === 'ALL' || (r.itemId && r.itemId.includes(itemFilter)) || (itemFilter === 'NONE' && !r.itemId);
            return matchSearch && matchStatus && matchItem;
        });
    }, [warehouseRoutings, search, statusFilter, itemFilter]);

    // Every stage with its work center, machine, time, QC and standard cost
    const stageDetails = useMemo(() => {
        const wcById = new Map(workCenters.map((w) => [String(w.id), w]));
        const out = new Map<string, {
            stages: (StageView & { wc?: WorkCenterResponse; machineLabel: string; hourly: number | null; unit: number | null; setup: number | null })[];
            runPerUnit: number; setup: number; unitCost: number; setupCost: number; costComplete: boolean; qcStages: number;
        }>();
        for (const r of routings) {
            const stages = (Array.isArray(r.stages) ? r.stages : []).map((raw, idx) => {
                const v = toStageView(raw, idx);
                const wc = wcById.get(v.workCenterId);
                const m = (wc?.machines || []).find((x) => String(x.id) === v.machineId);
                return {
                    ...v,
                    wc,
                    machineLabel: m ? [m.machine_code, m.name].filter(Boolean).join(' · ') : v.machineName,
                    hourly: stageHourlyCost(wc, costInput(v)),
                    unit: stageUnitCost(wc, costInput(v)),
                    setup: stageSetupCost(wc, costInput(v)),
                };
            }).sort((a, b) => a.seq - b.seq);
            out.set(r.id, {
                stages,
                runPerUnit: stages.reduce((s, st) => s + (st.stageType === 'JOB_WORK' ? 0 : runMinutesPerUnit(costInput(st))), 0),
                setup: stages.reduce((s, st) => s + (st.stageType === 'JOB_WORK' ? 0 : st.setupMinutes), 0),
                unitCost: stages.reduce((s, st) => s + (st.unit || 0), 0),
                setupCost: stages.reduce((s, st) => s + (st.setup || 0), 0),
                costComplete: stages.every((st) => st.unit != null),
                qcStages: stages.filter((st) => st.qcRequired).length,
            });
        }
        return out;
    }, [routings, workCenters]);

    const toggleExpanded = (id: string) => setExpanded((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });
    const allExpanded = filtered.length > 0 && filtered.every((r) => expanded.has(r.id));

    const stats = useMemo(() => {
        const total = warehouseRoutings.length;
        const active = warehouseRoutings.filter((r) => r.status === 'ACTIVE').length;
        const draft = warehouseRoutings.filter((r) => r.status === 'DRAFT').length;
        const obsolete = warehouseRoutings.filter((r) => r.status === 'OBSOLETE').length;
        const totalTime = warehouseRoutings.reduce((sum, r) => sum + r.totalTimeMinutes, 0);
        return { total, active, draft, obsolete, totalTime };
    }, [warehouseRoutings]);

    const handleDuplicate = async (routing: Routing) => {
        try {
            // Every stage field is copied (work center, machine, QC, crew, time basis, job work, materials);
            // the copy's stages get new ids
            const newStages = routing.stages.map(({ id: _stageId, ...s }: any, idx: number) => ({
                ...s,
                sequence: idx + 1,
                sequence_no: idx + 1,
                stageNumber: idx + 1,
            }));

            const newCode = `${routing.code}-COPY`.slice(0, 100);

            const payload = {
                name: `${routing.name} (Copy)`.slice(0, 255),
                code: newCode,
                itemId: routing.itemId || null,
                itemName: routing.itemName || null,
                version: routing.version || '1.0',
                status: 'DRAFT' as const,
                stages: newStages,
                totalTimeMinutes: routing.totalTimeMinutes || 0,
                isActive: true,
                warehouseId: selectedWarehouseId || null,
            };

            const res = await createRouting(payload);
            if (res.success && res.data) {
                toast.success(`"${routing.name}" duplicated successfully as DRAFT`);
                fetchData();
            } else {
                toast.error(res.message || 'Failed to duplicate routing');
            }
        } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Failed to duplicate routing');
        }
    };

    const confirmDelete = async () => {
        if (!deleteConfirm) return;
        try {
            const res = await deleteRouting(deleteConfirm.id);
            if (res.success) {
                toast.success(`"${deleteConfirm.name}" deleted successfully`);
                fetchData();
            } else {
                toast.error(res.message || 'Failed to delete routing');
            }
        } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Failed to delete routing');
        } finally {
            setDeleteConfirm(null);
        }
    };

    const hasFilters = search || statusFilter !== 'ALL' || itemFilter !== 'ALL';

    return (
        <AppLayout>
            <div className="p-6 space-y-5 w-full">
                {/* Header */}
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => navigate(-1)}
                            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#e2e8f0] text-[#64748b] transition-colors cursor-pointer"
                        >
                            <i className="ri-arrow-left-line text-base" />
                        </button>
                        <div>
                            <h2 className="text-xl font-bold text-[#1e293b]">Routing Master</h2>
                            <p className="text-sm text-[#64748b] mt-0.5">Define production stage sequences for items</p>
                        </div>
                    </div>
                    <button
                        onClick={() => navigate('/masters/routing/new')}
                        className="flex items-center gap-2 h-9 px-4 rounded-lg bg-[#4f46e5] text-white text-sm font-semibold hover:bg-indigo-700 transition-colors cursor-pointer whitespace-nowrap"
                    >
                        <i className="ri-add-line" /> New Routing
                    </button>
                </div>

                {/* Stats */}
                <MasterStatsRow
                    gridClassName="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3"
                    valueClassName="text-lg"
                    stats={[
                        { label: 'Total Routings', value: stats.total, icon: 'ri-git-branch-line', bg: 'bg-indigo-50', color: 'text-[#4f46e5]' },
                        { label: 'Active', value: stats.active, icon: 'ri-checkbox-circle-line', bg: 'bg-emerald-50', color: 'text-emerald-600' },
                        { label: 'Draft', value: stats.draft, icon: 'ri-draft-line', bg: 'bg-amber-50', color: 'text-amber-600' },
                        { label: 'Obsolete', value: stats.obsolete, icon: 'ri-archive-line', bg: 'bg-slate-100', color: 'text-slate-500' },
                        { label: 'Total Time', value: formatMinutes(stats.totalTime), icon: 'ri-time-line', bg: 'bg-blue-50', color: 'text-blue-600' },
                    ]}
                />

                {/* Filters */}
                <MasterFilters
                    search={search}
                    onSearchChange={setSearch}
                    searchPlaceholder="Search by name or code..."
                    filters={[
                        {
                            value: statusFilter,
                            onChange: (val) => setStatusFilter(val),
                            options: [
                                { value: 'ALL', label: 'All Status' },
                                { value: 'ACTIVE', label: 'Active' },
                                { value: 'DRAFT', label: 'Draft' },
                                { value: 'OBSOLETE', label: 'Obsolete' },
                            ],
                        },
                        {
                            value: itemFilter,
                            onChange: (val) => setItemFilter(val),
                            options: [
                                { value: 'ALL', label: 'All Items' },
                                { value: 'NONE', label: '— Generic —' },
                                ...filteredItemsList.map((i) => ({ value: i.id, label: i.name })),
                            ],
                        },
                    ]}
                    hasActiveFilters={hasFilters}
                    onClearFilters={() => {
                        setSearch('');
                        setStatusFilter('ALL');
                        setItemFilter('ALL');
                    }}
                />

                {/* Table — each routing opens into its stages */}
                <div className="bg-white border border-[#e2e8f0] rounded-xl overflow-hidden">
                    <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-[#e2e8f0]">
                        <p className="text-xs text-[#64748b]">Open a routing to see each stage's work center, machine, crew, time, QC and standard cost</p>
                        <button
                            type="button"
                            onClick={() => setExpanded(allExpanded ? new Set() : new Set(filtered.map((r) => r.id)))}
                            className="h-7 px-2.5 rounded-md border border-[#e2e8f0] text-xs font-semibold text-[#475569] hover:bg-[#f8fafc] cursor-pointer whitespace-nowrap"
                        >
                            <i className={allExpanded ? 'ri-contract-up-down-line' : 'ri-expand-up-down-line'} /> {allExpanded ? 'Collapse all' : 'Expand all'}
                        </button>
                    </div>
                    <div className="overflow-x-auto">
                    <table className="w-full min-w-max text-sm">
                        <thead>
                            <tr className="bg-[#f8fafc] border-b border-[#e2e8f0]">
                                {['', 'Code', 'Routing Name', 'Linked Item', 'Stages', 'Std Time', 'Std Cost / Unit', 'Version', 'Status', 'Actions'].map((h, i) => (
                                    <th key={h || i} className="text-left px-4 py-3 text-xs font-semibold text-[#64748b] uppercase tracking-wide whitespace-nowrap">
                                        {h}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading ? (
                                <tr>
                                    <td colSpan={10} className="px-4 py-12 text-center">
                                        <i className="ri-loader-4-line text-4xl text-indigo-500 animate-spin block mb-2" />
                                        <p className="text-[#94a3b8] text-sm">Loading routings...</p>
                                    </td>
                                </tr>
                            ) : filtered.map((r, idx) => {
                                const badge = STATUS_BADGE[r.status] || STATUS_BADGE.DRAFT;
                                const detail = stageDetails.get(r.id);
                                const isOpen = expanded.has(r.id);
                                return (
                                    <Fragment key={r.id}>
                                    <tr
                                        className={`border-b border-[#f1f5f9] hover:bg-[#f8fafc] transition-colors ${idx % 2 !== 0 ? 'bg-[#f8fafc]/40' : ''}`}
                                    >
                                        <td className="pl-3 pr-0 py-3 w-8">
                                            <button
                                                type="button"
                                                onClick={() => toggleExpanded(r.id)}
                                                className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-indigo-50 text-[#64748b] hover:text-[#4f46e5] cursor-pointer"
                                                title={isOpen ? 'Hide stages' : 'Show stages'}
                                            >
                                                <i className={`${isOpen ? 'ri-arrow-down-s-line' : 'ri-arrow-right-s-line'} text-base`} />
                                            </button>
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <span className="font-mono text-xs text-[#64748b]">{r.code}</span>
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <p className="font-medium text-[#1e293b]">{r.name}</p>
                                        </td>
                                        <td className="px-4 py-3">
                                            {(() => {
                                                const rawNames = r.itemName ? r.itemName.split(',').map((s: string) => s.trim()).filter(Boolean) : [];
                                                const itemNames = Array.from(new Set(rawNames));
                                                if (itemNames.length === 0) {
                                                    return <span className="text-sm text-[#94a3b8] italic">— Generic —</span>;
                                                }
                                                if (itemNames.length === 1) {
                                                    return <span className="text-sm font-medium text-[#1e293b]">{itemNames[0]}</span>;
                                                }
                                                return (
                                                    <div className="flex items-center gap-1.5 flex-wrap max-w-xs">
                                                        <span className="text-xs font-medium px-2 py-0.5 rounded-md bg-indigo-50 border border-indigo-200 text-indigo-700 truncate max-w-[140px]">
                                                            {itemNames[0]}
                                                        </span>
                                                        <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded bg-slate-100 text-slate-600" title={itemNames.join(', ')}>
                                                            +{itemNames.length - 1} more
                                                        </span>
                                                    </div>
                                                );
                                            })()}
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <button type="button" onClick={() => toggleExpanded(r.id)} className="text-left cursor-pointer">
                                                <span className="inline-flex items-center gap-1.5 text-sm font-medium text-[#1e293b]">
                                                    <i className="ri-file-list-line text-xs text-[#64748b]" />
                                                    {r.stages.length} stage{r.stages.length === 1 ? '' : 's'}
                                                </span>
                                                {detail && detail.qcStages > 0 && (
                                                    <span className="block text-[11px] text-emerald-700"><i className="ri-shield-check-line" /> {detail.qcStages} with QC</span>
                                                )}
                                            </button>
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            {detail ? (
                                                <>
                                                    <span className="text-sm font-semibold text-[#1e293b]">{formatMinutes(detail.runPerUnit)}<span className="text-xs font-normal text-[#94a3b8]"> / unit</span></span>
                                                    <span className="block text-[11px] text-[#94a3b8]">+ setup {formatMinutes(detail.setup)} per run</span>
                                                </>
                                            ) : (
                                                <span className="text-sm font-semibold text-[#1e293b]">{formatMinutes(r.totalTimeMinutes)}</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            {detail && detail.stages.length > 0 ? (
                                                <>
                                                    <span className="text-sm font-semibold text-[#1e293b]">{money(detail.unitCost)}</span>
                                                    <span className="block text-[11px] text-[#94a3b8]">+ setup {money(detail.setupCost)} per run</span>
                                                    {!detail.costComplete && (
                                                        <span className="block text-[11px] text-amber-600" title="A stage has no work center (or no machine on a machine work center), so its cost is not counted">
                                                            <i className="ri-error-warning-line" /> incomplete
                                                        </span>
                                                    )}
                                                </>
                                            ) : <span className="text-[#94a3b8]">—</span>}
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <span className="font-mono text-xs text-[#64748b]">{r.version}</span>
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <span
                                                className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border ${badge.cls}`}
                                            >
                                                <span className={`w-1.5 h-1.5 rounded-full ${badge.dot}`} />
                                                {r.status}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <div className="flex items-center gap-1">
                                                {(() => {
                                                    const parsed = typeof r.stages === 'string' ? (() => { try { return JSON.parse(r.stages); } catch { return []; } })() : r.stages;
                                                    const hasDraftStage = Array.isArray(parsed) && parsed.some((s: any) => s.status === 'DRAFT');
                                                    if (hasDraftStage) {
                                                        return (
                                                            <button
                                                                onClick={() => navigate(`/masters/routing/${r.id}/edit`)}
                                                                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200 transition-colors cursor-pointer whitespace-nowrap shadow-xs"
                                                                title="Incomplete BOM Mapping — Click to Resume"
                                                            >
                                                                Resume →
                                                            </button>
                                                        );
                                                    }
                                                    return (
                                                        <button
                                                            onClick={() => navigate(`/masters/routing/${r.id}/edit`)}
                                                            className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium text-[#4f46e5] hover:bg-[#4f46e5]/10 transition-colors cursor-pointer whitespace-nowrap"
                                                            title="View / Edit"
                                                        >
                                                            <i className="ri-edit-line" />
                                                            Edit
                                                        </button>
                                                    );
                                                })()}
                                                <button
                                                    onClick={() => handleDuplicate(r)}
                                                    className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-indigo-50 text-[#64748b] hover:text-[#4f46e5] transition-colors cursor-pointer"
                                                    title="Duplicate"
                                                >
                                                    <i className="ri-file-copy-line text-sm" />
                                                </button>
                                                <button
                                                    onClick={() => setDeleteConfirm(r)}
                                                    className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50 text-[#64748b] hover:text-red-500 transition-colors cursor-pointer"
                                                    title="Delete"
                                                >
                                                    <i className="ri-delete-bin-line text-sm" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                    {isOpen && detail && (
                                        <tr className="border-b border-[#e2e8f0] bg-[#f8fafc]">
                                            <td colSpan={10} className="px-4 py-3">
                                                {detail.stages.length === 0 ? (
                                                    <p className="text-xs text-[#94a3b8] px-1">No stages defined</p>
                                                ) : (
                                                    <div className="bg-white border border-[#e2e8f0] rounded-lg overflow-hidden">
                                                        <table className="w-full text-xs">
                                                            <thead>
                                                                <tr className="bg-[#f1f5f9] text-[#64748b]">
                                                                    {['#', 'Stage', 'Work Center', 'Machine', 'Crew', 'Std Time', 'QC', 'Std Cost', 'Materials', 'Status'].map((h) => (
                                                                        <th key={h} className="text-left px-3 py-2 font-semibold uppercase tracking-wide whitespace-nowrap">{h}</th>
                                                                    ))}
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {detail.stages.map((st) => {
                                                                    const jobWork = st.stageType === 'JOB_WORK';
                                                                    const needsMachine = !jobWork && st.wc?.machine_required;
                                                                    return (
                                                                        <tr key={`${r.id}-${st.seq}`} className="border-t border-[#f1f5f9] align-top">
                                                                            <td className="px-3 py-2.5">
                                                                                <span className="w-6 h-6 inline-flex items-center justify-center rounded-full bg-indigo-50 text-[#4f46e5] font-bold">{st.seq}</span>
                                                                            </td>
                                                                            <td className="px-3 py-2.5 min-w-[150px]">
                                                                                <p className="font-semibold text-[#1e293b]">{st.name}</p>
                                                                                {jobWork ? (
                                                                                    <span className="inline-flex items-center gap-1 mt-0.5 px-1.5 py-0.5 rounded bg-purple-50 text-purple-700 border border-purple-200 font-semibold">
                                                                                        <i className="ri-truck-line" /> Job work{st.subcontractorName ? ` · ${st.subcontractorName}` : ''}
                                                                                    </span>
                                                                                ) : (
                                                                                    <span className="text-[#94a3b8]">In-house</span>
                                                                                )}
                                                                            </td>
                                                                            <td className="px-3 py-2.5 whitespace-nowrap">
                                                                                {jobWork ? <span className="text-[#94a3b8]">At sub-contractor</span>
                                                                                    : st.wc ? (
                                                                                        <>
                                                                                            <p className="font-medium text-[#1e293b]">{st.wc.name}</p>
                                                                                            <p className="text-[#94a3b8]">
                                                                                                {st.wc.code}{st.wc.type ? ` · ${WC_TYPE_LABEL[String(st.wc.type).toUpperCase()] || st.wc.type}` : ''}
                                                                                                {st.wc.is_active === false && <span className="text-rose-600"> · inactive</span>}
                                                                                            </p>
                                                                                        </>
                                                                                    ) : st.workCenterName ? (
                                                                                        <>
                                                                                            <p className="font-medium text-[#1e293b]">{st.workCenterName}</p>
                                                                                            <p className="text-amber-600">not found in work centers</p>
                                                                                        </>
                                                                                    ) : <span className="text-rose-600"><i className="ri-error-warning-line" /> Not assigned</span>}
                                                                            </td>
                                                                            <td className="px-3 py-2.5 whitespace-nowrap">
                                                                                {jobWork ? <span className="text-[#94a3b8]">—</span>
                                                                                    : st.machineLabel ? <span className="text-[#475569]">{st.machineLabel}</span>
                                                                                        : needsMachine ? <span className="text-amber-600"><i className="ri-error-warning-line" /> Required, not set</span>
                                                                                            : <span className="text-[#94a3b8]">Manual work</span>}
                                                                            </td>
                                                                            <td className="px-3 py-2.5 whitespace-nowrap text-[#475569]">
                                                                                {jobWork ? '—' : `${st.operatorsRequired} ${st.operatorsRequired === 1 ? 'person' : 'persons'}`}
                                                                            </td>
                                                                            <td className="px-3 py-2.5 whitespace-nowrap">
                                                                                {jobWork ? <span className="text-[#94a3b8]">Sub-contractor's time</span> : (
                                                                                    <>
                                                                                        <p className="font-medium text-[#1e293b]">
                                                                                            Run {formatMinutes(st.runMinutes)} {st.timeBasis === 'PER_BATCH' ? `per batch of ${st.batchSize}` : 'per unit'}
                                                                                        </p>
                                                                                        <p className="text-[#94a3b8]">
                                                                                            Setup {formatMinutes(st.setupMinutes)}
                                                                                            {st.timeBasis === 'PER_BATCH' && ` · ${formatMinutes(runMinutesPerUnit(costInput(st)))}/unit`}
                                                                                        </p>
                                                                                    </>
                                                                                )}
                                                                            </td>
                                                                            <td className="px-3 py-2.5 whitespace-nowrap">
                                                                                {st.qcRequired ? (
                                                                                    <>
                                                                                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 font-semibold">
                                                                                            <i className="ri-shield-check-line" /> Required
                                                                                        </span>
                                                                                        <p className={st.qcChecklistName ? 'text-[#64748b] mt-0.5' : 'text-amber-600 mt-0.5'}>
                                                                                            {st.qcChecklistName || 'No checklist'}
                                                                                        </p>
                                                                                    </>
                                                                                ) : <span className="text-[#94a3b8]">No QC</span>}
                                                                            </td>
                                                                            <td className="px-3 py-2.5 whitespace-nowrap">
                                                                                {jobWork ? (
                                                                                    <>
                                                                                        <p className="font-semibold text-[#1e293b]">{money(st.jobRate)}/unit</p>
                                                                                        <p className="text-[#94a3b8]">job rate</p>
                                                                                    </>
                                                                                ) : st.hourly != null ? (
                                                                                    <>
                                                                                        <p className="font-semibold text-[#1e293b]">{st.unit != null ? `${money(st.unit)}/unit` : '—'}</p>
                                                                                        <p className="text-[#94a3b8]">{money(st.hourly)}/hr · setup {money(st.setup || 0)}</p>
                                                                                    </>
                                                                                ) : (
                                                                                    <span className="text-amber-600" title="Needs a work center, and a machine on a machine work center">—</span>
                                                                                )}
                                                                            </td>
                                                                            <td className="px-3 py-2.5 whitespace-nowrap text-[#475569]">
                                                                                {st.materials > 0 ? `${st.materials} BOM item${st.materials === 1 ? '' : 's'}` : <span className="text-[#94a3b8]">—</span>}
                                                                            </td>
                                                                            <td className="px-3 py-2.5 whitespace-nowrap">
                                                                                <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-semibold border ${st.status === 'DRAFT' ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}`}>
                                                                                    {st.status === 'DRAFT' ? 'Draft' : 'Active'}
                                                                                </span>
                                                                            </td>
                                                                        </tr>
                                                                    );
                                                                })}
                                                            </tbody>
                                                            <tfoot>
                                                                <tr className="border-t border-[#e2e8f0] bg-[#f8fafc] font-semibold text-[#1e293b]">
                                                                    <td className="px-3 py-2" colSpan={5}>Routing total · {detail.stages.length} stage{detail.stages.length === 1 ? '' : 's'}, {detail.qcStages} with QC</td>
                                                                    <td className="px-3 py-2 whitespace-nowrap">
                                                                        {formatMinutes(detail.runPerUnit)}/unit
                                                                        <span className="block font-normal text-[#94a3b8]">+ setup {formatMinutes(detail.setup)} per run</span>
                                                                    </td>
                                                                    <td className="px-3 py-2" />
                                                                    <td className="px-3 py-2 whitespace-nowrap">
                                                                        {money(detail.unitCost)}/unit
                                                                        <span className="block font-normal text-[#94a3b8]">+ setup {money(detail.setupCost)} per run</span>
                                                                    </td>
                                                                    <td className="px-3 py-2" colSpan={2} />
                                                                </tr>
                                                            </tfoot>
                                                        </table>
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    )}
                                    </Fragment>
                                );
                            })}
                            {!isLoading && filtered.length === 0 && (
                                <tr>
                                    <td colSpan={10} className="px-4 py-12 text-center">
                                        <i className="ri-git-branch-line text-4xl text-[#e2e8f0] block mb-2" />
                                        <p className="text-[#94a3b8] text-sm">No routings found</p>
                                        {hasFilters && <p className="text-xs text-[#94a3b8] mt-1">Try adjusting your filters</p>}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                    </div>
                    <div className="px-4 py-3 border-t border-[#e2e8f0]">
                        <p className="text-xs text-[#94a3b8]">Showing {filtered.length} of {warehouseRoutings.length} routings</p>
                    </div>
                </div>
            </div>

            <ConfirmDialog
                open={!!deleteConfirm}
                title="Delete Routing"
                message={`Delete "${deleteConfirm?.name}"? This action cannot be undone.`}
                variant="danger"
                confirmLabel="Yes, Delete (Y)"
                onConfirm={confirmDelete}
                onCancel={() => setDeleteConfirm(null)}
            />
        </AppLayout>
    );
}